import React, { useEffect, useRef, useState } from "react";
import { COUNTRIES, BUY_LINKS } from "../../data/content";

/**
 * Buy Now dropdown used on every book card.
 * - Custom controlled state (no Radix) to avoid touch-through bug on mobile
 * - Flips up if not enough room below the button
 * - All items are <a target="_blank"> so tap-open doesn't synthesise a click on an item
 */
export default function BuyDropdown({ book }) {
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState("down");
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);

  const MENU_ESTIMATED_HEIGHT = 320;

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < MENU_ESTIMATED_HEIGHT && spaceAbove > spaceBelow) {
        setDirection("up");
      } else {
        setDirection("down");
      }
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const menuPositionClass =
    direction === "up"
      ? "bottom-full mb-3 origin-bottom-left"
      : "top-full mt-3 origin-top-left";

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn-buy"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Buy ${book.title}`}
        data-testid={`buy-now-${book.id}`}
      >
        Buy Now
        <span
          className="inline-block transition-transform duration-300"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          {open ? "↑" : "↗"}
        </span>
      </button>

      <div
        role="menu"
        data-direction={direction}
        className={`absolute z-40 left-0 min-w-[280px] bg-[color:var(--paper)] text-[color:var(--ink)] border border-stone-900/15 shadow-[0_20px_60px_rgba(28,25,23,0.15)] transition-all duration-200 ${menuPositionClass} ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
        data-testid={`country-menu-${book.id}`}
      >
        <div className="px-4 pt-4 pb-2 text-[10px] tracking-[0.28em] uppercase text-stone-500 font-medium">
          Choose your country
        </div>
        <div className="h-px bg-stone-900/10" />
        <ul className="max-h-[min(65vh,540px)] overflow-y-auto py-1">
          {COUNTRIES.map((c) => {
            const url = BUY_LINKS[book.id]?.[c.code];
            return (
              <li key={c.code}>
                <a
                  href={url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 text-sm flex items-center justify-between hover:bg-stone-900 hover:text-[color:var(--paper)] focus:bg-stone-900 focus:text-[color:var(--paper)] transition-colors duration-150 outline-none"
                  data-testid={`country-option-${book.id}-${c.code}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="text-lg leading-none">{c.flag}</span>
                    <span>{c.label}</span>
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase opacity-60">
                    {c.store}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
